import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { AutoRepliesConfig } from '@app/components/Admin/types';
import {
  GET_CASE_ASSIGNMENT_SUMMARY,
  PATCH_OPERATOR,
} from '@app/constants/endpoints';
import { DISPLAY_UNIQUE_ID_WITH_USER_NAME } from '@app/constants/permissions';
import {
  QUERY_KEYS,
  OPERATORS_BULK_UPLOAD_STATUS_QUERY_KEY,
} from '@app/constants/service';
import { AUTO_REPLY_TYPES } from '@app/constants/user-mgmt';
import { formatAutoReplyResponse } from '@app/helpers/auto-replies';
import usePermissionVerify, {
  verifyPermission,
} from '@app/hooks/permission-verify';
import { fetchData, patchData, postData } from '@app/hooks/react-query-helpers';
import { NAMESPACE } from '@app/i18n';
import queryClient from '@app/query-client';
import {
  fetchOperators,
  fetchBulkUploadStatus,
} from '@app/services/operator/handler';
import { User } from '@app/types/api/user';
import { CaseTypeEnum } from '@app/types/create-case';
import { toaster } from '@chakra-primitives/toaster';

type AvailabilityState = 'AWAY' | 'ACTIVE';

export interface AutoReplyItem {
  id?: string;
  type?: string;
  active?: boolean;
  message?: { during?: { value?: string } };
}

interface UserAutoReplies {
  availabilityState?: string | null;
  generalAutoReplies: AutoReplyItem[];
}

const POST_OPERATOR_AVAILABILITY = (operatorId: string) =>
  `/api/operators/${operatorId}/availability`;

const GET_USER_AUTOREPLIES = (userId: string) =>
  `/api/users/${userId}/autoReplies`;

export const USER_AUTOREPLIES_QUERY_KEY = (userId: string) =>
  ['userAutoReplies', userId] as const;

export const useUserAutoReplies = (userId: string) =>
  useQuery<UserAutoReplies>({
    queryKey: USER_AUTOREPLIES_QUERY_KEY(userId),
    queryFn: async () => {
      const res = (await fetchData(GET_USER_AUTOREPLIES(userId))) as
        | (AutoRepliesConfig & { availabilityState?: string | null })
        | null;
      const replies = formatAutoReplyResponse(
        res as AutoRepliesConfig,
        `USER#${userId}`,
      ) as AutoReplyItem[];
      return {
        availabilityState: res?.availabilityState ?? null,
        generalAutoReplies: replies.filter(
          (r) => r.type === AUTO_REPLY_TYPES.GENERAL,
        ),
      };
    },
    enabled: !!userId,
    // Cache survives logout, so refetch on mount for fresh status on re-login.
    refetchOnMount: true,
  });

export const useUpdateOperatorAvailability = (operatorId: string) => {
  const qc = useQueryClient();
  const key = USER_AUTOREPLIES_QUERY_KEY(operatorId);

  return useMutation({
    mutationFn: async (availabilityState: AvailabilityState) =>
      postData(POST_OPERATOR_AVAILABILITY(operatorId), {
        availabilityState,
      }),
    onMutate: async (availabilityState: AvailabilityState) => {
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<UserAutoReplies>(key);
      // Only seed with a baseline; on a cold cache we'd overwrite the in-flight message with an empty list.
      if (previous) {
        qc.setQueryData<UserAutoReplies>(key, {
          ...previous,
          availabilityState,
        });
      }
      return { previous };
    },
    onError: (_error, _availabilityState, context) => {
      if (context?.previous) {
        qc.setQueryData(key, context.previous);
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: key });
    },
  });
};

export const useUpdateOperatorExposureSkills = (operatorId: string) => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (exposureSkills: string[]) =>
      patchData(PATCH_OPERATOR(operatorId), { exposureSkills }),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: [QUERY_KEYS.OPERATORS],
        refetchType: 'active',
      });
    },
  });
};

export const useUpdateOperatorAllowedCaseTypes = (operatorId: string) => {
  const qc = useQueryClient();
  const { t } = useTranslation(NAMESPACE.TEAM_MANAGEMENT);

  return useMutation({
    mutationFn: async (allowedCaseTypes: CaseTypeEnum[]) =>
      patchData(PATCH_OPERATOR(operatorId), { allowedCaseTypes }),
    onSuccess: () => {
      toaster.create({
        type: 'success',
        description: t('users.assignAllowedCaseTypes.updateSuccessToast'),
      });
    },
    onError: () => {
      toaster.create({
        type: 'error',
        description: t('users.assignAllowedCaseTypes.updateErrorToast'),
      });
    },
    // This key prefix also matches the bulk-upload-status and case-assignment-summary queries.
    onSettled: () => {
      qc.invalidateQueries({
        queryKey: [QUERY_KEYS.OPERATORS],
        refetchType: 'active',
      });
    },
  });
};

const buildOperatorsQuery = (
  customFilters: {
    searchText?: string;
    ids?: string[];
    groups?: string[];
    exposureSkills?: string[];
    leadsOfGroups?: string[];
    status?: string;
    order?: string;
    sort?: string;
    limit?: number;
    includeGroupDetails?: boolean;
    includeOktaUserId?: boolean;
  },
  options?: {
    enabled?: boolean;
  },
) => {
  const filters = {
    limit: 32,
    role: 'operator',
    status: 'active',
    sort: 'profile.firstName',
    order: 'ASC',
    ...customFilters,
  };

  return {
    queryKey: [QUERY_KEYS.OPERATORS, filters],
    queryFn: ({ pageParam = 1 }) => fetchOperators(filters, pageParam),
    getNextPageParam: filters.searchText?.trim()
      ? undefined
      : (lastPage: { operators: User[] }, pages: { operators: User[] }[]) => {
          if ((lastPage?.operators?.length || 0) < filters.limit)
            return undefined;
          return (pages?.length || 0) + 1;
        },
    ...options,
  };
};

// for react component
export const useOperatorsQuery = (
  customFilters: {
    searchText?: string;
    ids?: string[];
    groups?: string[];
    exposureSkills?: string[];
    leadsOfGroups?: string[];
    status?: string;
    order?: string;
    sort?: string;
    limit?: number;
    includeGroupDetails?: boolean;
    from?: string;
  },
  options?: {
    enabled?: boolean;
  },
) => {
  const { DISPLAY_UNIQUE_ID_WITH_USER_NAME: showUniqueId } =
    usePermissionVerify([DISPLAY_UNIQUE_ID_WITH_USER_NAME]);

  const filtersWithPermission = {
    ...customFilters,
    includeOktaUserId: !!showUniqueId,
  };

  return useInfiniteQuery(buildOperatorsQuery(filtersWithPermission, options));
};

const BULK_UPLOAD_STATUS_POLL_INTERVAL_MS = 5000;

export interface CaseAssignmentCounts {
  open: number;
  closed: number;
}

export interface CaseAssignmentSummary {
  reassignmentEnabled: boolean;
  maxCasesPerRequest: number;
  primary: CaseAssignmentCounts;
  secondary: CaseAssignmentCounts;
}

export const useUserCaseAssignmentSummary = (userId?: string, enabled = true) =>
  useQuery<CaseAssignmentSummary>({
    queryKey: QUERY_KEYS.CASE_ASSIGNMENT_SUMMARY(userId ?? ''),
    queryFn: () => fetchData(GET_CASE_ASSIGNMENT_SUMMARY(userId as string)),
    enabled: enabled && !!userId,
  });

export const useBulkUploadStatus = (enabled = true) =>
  useQuery({
    queryKey: [...OPERATORS_BULK_UPLOAD_STATUS_QUERY_KEY],
    queryFn: fetchBulkUploadStatus,
    enabled,
    refetchInterval: (data) =>
      data?.inProgress ? BULK_UPLOAD_STATUS_POLL_INTERVAL_MS : false,
  });

// for react-router loader
export const prefetchOperatorsQuery = (
  customFilters: {
    searchText?: string;
    ids?: string[];
    groups?: string[];
    status?: string;
    order?: string;
    sort?: string;
    limit?: number;
    includeGroupDetails?: boolean;
  },
  options?: {
    enabled?: boolean;
    staleTime?: number;
  },
) => {
  // we use to verifyPermission retrieve data because hook cannot be used outside react component
  const { DISPLAY_UNIQUE_ID_WITH_USER_NAME: showUniqueId } = verifyPermission([
    DISPLAY_UNIQUE_ID_WITH_USER_NAME,
  ]);
  const filtersWithPermission = {
    ...customFilters,
    includeOktaUserId: !!showUniqueId,
  };
  queryClient.prefetchInfiniteQuery(
    buildOperatorsQuery(filtersWithPermission, options),
  );
};
